import * as THREE from 'three';
import { createMatteMaterial, createGlowMaterial } from '../../../visual';
import type { ShipSkin } from '../skins/ShipSkin';
import {
  createTaperedSlab,
  type EngineMount,
  type ShipBuildResult
} from './common';

export function buildPirateInterceptor(
  parent: THREE.Object3D,
  skin: ShipSkin
): ShipBuildResult {
  const strobes: THREE.Mesh[] = [];

  buildNose(parent, skin);
  buildRearBody(parent, skin);
  buildCockpit(parent, skin);
  buildWings(parent, skin);
  buildStabilizer(parent, skin);
  const engines = buildEngines(parent, skin);
  buildStrobes(parent, skin, strobes);

  return { engines, strobes, collisionRadius: 1.8 };
}

function buildNose(parent: THREE.Object3D, skin: ShipSkin): void {
  const creamMat = createMatteMaterial({ color: skin.hull, roughness: 0.6 });
  const redMat = createMatteMaterial({ color: skin.primary, roughness: 0.38 });
  const redDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.55
  });
  const goldMat = createMatteMaterial({
    color: skin.accent,
    roughness: 0.45,
    emissive: skin.accent,
    emissiveIntensity: 0.02
  });
  const darkMat = createMatteMaterial({ color: skin.trim, roughness: 0.7 });

  const slab = new THREE.Mesh(
    createTaperedSlab(0.34, 0.58, 0.26, 2.35),
    creamMat
  );
  slab.position.set(0, 0.08, -1.2);

  const topPanel = new THREE.Mesh(
    createTaperedSlab(0.2, 0.42, 0.04, 2.1),
    redMat
  );
  topPanel.position.set(0, 0.22, -1.2);

  const topStripe = new THREE.Mesh(
    new THREE.BoxGeometry(0.05, 0.015, 1.9),
    darkMat
  );
  topStripe.position.set(0, 0.24, -1.2);

  const tip = new THREE.Mesh(createTaperedSlab(0.28, 0.34, 0.23, 0.32), redMat);
  tip.position.set(0, 0.08, -2.52);

  const tipCap = new THREE.Mesh(
    new THREE.BoxGeometry(0.22, 0.18, 0.06),
    redDark
  );
  tipCap.position.set(0, 0.08, -2.7);

  const tipPitot = new THREE.Mesh(
    new THREE.CylinderGeometry(0.02, 0.025, 0.18, 8),
    darkMat
  );
  tipPitot.rotation.x = Math.PI / 2;
  tipPitot.position.set(0, 0.08, -2.85);

  const ventGeo = new THREE.BoxGeometry(0.045, 0.12, 1.55);
  const ventLeft = new THREE.Mesh(ventGeo, goldMat);
  ventLeft.position.set(-0.46, 0.08, -1.2);
  const ventRight = new THREE.Mesh(ventGeo, goldMat);
  ventRight.position.set(0.46, 0.08, -1.2);

  const ventTrimLeft = new THREE.Mesh(
    new THREE.BoxGeometry(0.01, 0.15, 1.6),
    darkMat
  );
  ventTrimLeft.position.set(-0.485, 0.08, -1.2);
  const ventTrimRight = ventTrimLeft.clone();
  ventTrimRight.position.x = 0.485;

  const belly = new THREE.Mesh(
    createTaperedSlab(0.32, 0.52, 0.08, 2.15),
    darkMat
  );
  belly.position.set(0, -0.12, -1.15);

  const bellyStripe = new THREE.Mesh(
    new THREE.BoxGeometry(0.06, 0.015, 1.8),
    redMat
  );
  bellyStripe.position.set(0, -0.168, -1.15);

  const spine = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.06, 2.0), darkMat);
  spine.position.set(0, 0.23, -1.2);

  parent.add(
    slab,
    topPanel,
    topStripe,
    tip,
    tipCap,
    tipPitot,
    ventLeft,
    ventRight,
    ventTrimLeft,
    ventTrimRight,
    belly,
    bellyStripe,
    spine
  );
}

function buildRearBody(parent: THREE.Object3D, skin: ShipSkin): void {
  const creamMat = createMatteMaterial({ color: skin.hull, roughness: 0.6 });
  const redMat = createMatteMaterial({ color: skin.primary, roughness: 0.38 });
  const redDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.55
  });
  const darkMat = createMatteMaterial({ color: skin.hullDark, roughness: 0.75 });
  const trimMat = createMatteMaterial({ color: skin.trim, roughness: 0.6 });

  const body = new THREE.Mesh(
    new THREE.SphereGeometry(0.58, 28, 20),
    creamMat
  );
  body.scale.set(1.1, 0.78, 1.28);
  body.position.set(0, 0.08, 0.26);

  const shoulderPlate = new THREE.Mesh(
    new THREE.BoxGeometry(0.95, 0.1, 0.65),
    redMat
  );
  shoulderPlate.position.set(0, 0.45, 0.28);

  const shoulderTrim = new THREE.Mesh(
    new THREE.BoxGeometry(0.98, 0.03, 0.05),
    darkMat
  );
  shoulderTrim.position.set(0, 0.5, 0.58);

  const bellyDark = new THREE.Mesh(
    new THREE.SphereGeometry(
      0.56,
      22,
      14,
      0,
      Math.PI * 2,
      Math.PI / 2,
      Math.PI / 2
    ),
    darkMat
  );
  bellyDark.scale.set(1.1, 0.55, 1.25);
  bellyDark.position.set(0, -0.02, 0.26);

  const rearPlate = new THREE.Mesh(
    new THREE.CylinderGeometry(0.56, 0.5, 0.28, 24),
    redMat
  );
  rearPlate.rotation.x = Math.PI / 2;
  rearPlate.scale.set(1.12, 0.78, 1);
  rearPlate.position.set(0, 0.07, 0.94);

  const rearPlateTrim = new THREE.Mesh(
    new THREE.CylinderGeometry(0.57, 0.57, 0.05, 24),
    redDark
  );
  rearPlateTrim.rotation.x = Math.PI / 2;
  rearPlateTrim.scale.set(1.12, 0.78, 1);
  rearPlateTrim.position.set(0, 0.07, 0.8);

  const seam = new THREE.Mesh(
    new THREE.TorusGeometry(0.56, 0.04, 8, 28),
    trimMat
  );
  seam.scale.set(1.1, 0.78, 1);
  seam.rotation.x = Math.PI / 2;
  seam.position.set(0, 0.07, 0.77);

  const hullSideLeft = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.3, 0.55),
    redDark
  );
  hullSideLeft.position.set(-0.6, 0.1, 0.38);
  const hullSideRight = hullSideLeft.clone();
  hullSideRight.position.x = 0.6;

  parent.add(
    body,
    shoulderPlate,
    shoulderTrim,
    bellyDark,
    rearPlate,
    rearPlateTrim,
    seam,
    hullSideLeft,
    hullSideRight
  );
}

function buildCockpit(parent: THREE.Object3D, skin: ShipSkin): void {
  const canopyMat = createMatteMaterial({
    color: skin.cockpit,
    roughness: 0.18,
    emissive: skin.cockpitGlow,
    emissiveIntensity: 0.03
  });
  const frameMat = createMatteMaterial({ color: skin.trim, roughness: 0.5 });
  const accentMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.4
  });

  const canopyBase = new THREE.Mesh(
    new THREE.TorusGeometry(0.36, 0.045, 10, 28),
    frameMat
  );
  canopyBase.rotation.x = Math.PI / 2;
  canopyBase.scale.set(0.94, 1.3, 1);
  canopyBase.position.set(0, 0.4, 0.04);

  const canopy = new THREE.Mesh(
    new THREE.SphereGeometry(0.36, 28, 18, 0, Math.PI * 2, 0, Math.PI / 2.05),
    canopyMat
  );
  canopy.scale.set(0.96, 1.05, 1.4);
  canopy.position.set(0, 0.44, 0.05);

  const canopyHighlight = new THREE.Mesh(
    new THREE.SphereGeometry(
      0.36,
      24,
      14,
      Math.PI * 1.45,
      Math.PI * 0.45,
      Math.PI * 0.15,
      Math.PI * 0.35
    ),
    createMatteMaterial({
      color: skin.cockpitGlow,
      roughness: 0.22,
      emissive: skin.cockpitGlow,
      emissiveIntensity: 0.1
    })
  );
  canopyHighlight.scale.set(0.96, 1.05, 1.4);
  canopyHighlight.position.set(0, 0.44, 0.05);

  const canopyFrame = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.45, 0.35),
    frameMat
  );
  canopyFrame.position.set(0, 0.55, 0.05);
  canopyFrame.rotation.x = 0.35;

  const canopyAccent = new THREE.Mesh(
    new THREE.BoxGeometry(0.55, 0.03, 0.04),
    accentMat
  );
  canopyAccent.position.set(0, 0.46, -0.42);

  parent.add(canopyBase, canopy, canopyHighlight, canopyFrame, canopyAccent);
}

function buildWings(parent: THREE.Object3D, skin: ShipSkin): void {
  const redMat = createMatteMaterial({ color: skin.primary, roughness: 0.4 });
  const darkRedMat = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.55
  });
  const creamMat = createMatteMaterial({ color: skin.hull, roughness: 0.6 });

  const wingShape = new THREE.Shape();
  wingShape.moveTo(0, 0);
  wingShape.lineTo(1.25, 0.42);
  wingShape.lineTo(1.25, 0.95);
  wingShape.lineTo(0.38, 1.05);
  wingShape.lineTo(0, 0.85);
  wingShape.closePath();

  const wingGeo = new THREE.ExtrudeGeometry(wingShape, {
    depth: 0.14,
    bevelEnabled: true,
    bevelSize: 0.045,
    bevelThickness: 0.045,
    bevelSegments: 2,
    steps: 1
  });
  wingGeo.translate(0, -0.5, -0.07);
  wingGeo.rotateX(Math.PI / 2);

  const wingletShape = new THREE.Shape();
  wingletShape.moveTo(0, 0);
  wingletShape.lineTo(0.6, 0);
  wingletShape.lineTo(0.48, 0.38);
  wingletShape.lineTo(0.08, 0.42);
  wingletShape.closePath();

  const wingletGeo = new THREE.ExtrudeGeometry(wingletShape, {
    depth: 0.08,
    bevelEnabled: true,
    bevelSize: 0.02,
    bevelThickness: 0.02,
    bevelSegments: 2
  });
  wingletGeo.translate(-0.3, 0, -0.04);

  const mkSide = (sign: 1 | -1): void => {
    const wing = new THREE.Mesh(wingGeo, redMat);
    wing.scale.x = sign;
    wing.position.set(sign * 0.52, 0.04, 0.3);

    const wingTopStripe = new THREE.Mesh(
      new THREE.BoxGeometry(1.0, 0.02, 0.25),
      creamMat
    );
    wingTopStripe.position.set(sign * 1.0, 0.17, 0.55);
    wingTopStripe.rotation.y = sign * -0.3;

    const wingLeadingEdge = new THREE.Mesh(
      new THREE.BoxGeometry(1.15, 0.07, 0.05),
      darkRedMat
    );
    wingLeadingEdge.position.set(sign * 1.0, 0.06, 0.05);
    wingLeadingEdge.rotation.y = sign * -0.32;

    const winglet = new THREE.Mesh(wingletGeo, darkRedMat);
    winglet.scale.x = sign;
    winglet.rotation.y = (sign * -Math.PI) / 2;
    winglet.position.set(sign * 1.74, 0.06, 0.58);

    parent.add(wing, wingTopStripe, wingLeadingEdge, winglet);
  };

  mkSide(1);
  mkSide(-1);
}

function buildStabilizer(parent: THREE.Object3D, skin: ShipSkin): void {
  const creamMat = createMatteMaterial({ color: skin.hull, roughness: 0.6 });
  const redMat = createMatteMaterial({ color: skin.primary, roughness: 0.4 });

  const finShape = new THREE.Shape();
  finShape.moveTo(0, 0);
  finShape.lineTo(0.6, 0);
  finShape.lineTo(0.35, 0.4);
  finShape.lineTo(0.05, 0.4);
  finShape.closePath();

  const finGeo = new THREE.ExtrudeGeometry(finShape, {
    depth: 0.05,
    bevelEnabled: true,
    bevelSize: 0.015,
    bevelThickness: 0.015,
    bevelSegments: 2
  });
  finGeo.translate(-0.325, 0, -0.025);
  finGeo.rotateY(Math.PI / 2);

  const fin = new THREE.Mesh(finGeo, creamMat);
  fin.position.set(0, 0.52, 0.7);

  const finTrim = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.04, 0.35),
    redMat
  );
  finTrim.position.set(0, 0.92, 0.8);

  parent.add(fin, finTrim);
}

function buildEngines(parent: THREE.Object3D, skin: ShipSkin): EngineMount[] {
  const housingMat = createMatteMaterial({
    color: skin.primary,
    roughness: 0.38
  });
  const housingDark = createMatteMaterial({
    color: skin.primaryDark,
    roughness: 0.55
  });
  const rimMat = createMatteMaterial({ color: skin.trim, roughness: 0.55 });
  const innerMat = createMatteMaterial({
    color: skin.hullDark,
    roughness: 0.75
  });
  const coreMat = createGlowMaterial(skin.glow, 0.85, true);
  const plumeMat = createGlowMaterial(skin.plasma, 0.35, true);
  const louverMat = createMatteMaterial({ color: skin.trim, roughness: 0.65 });

  const mounts: EngineMount[] = [];

  const make = (x: number, scale: number): EngineMount => {
    const z = 1.18;
    const y = 0.05;

    const housing = new THREE.Mesh(
      new THREE.CylinderGeometry(0.28 * scale, 0.31 * scale, 0.72, 24),
      housingMat
    );
    housing.rotation.x = Math.PI / 2;
    housing.position.set(x, y, z);

    const housingRingFront = new THREE.Mesh(
      new THREE.TorusGeometry(0.3 * scale, 0.03, 8, 26),
      housingDark
    );
    housingRingFront.position.set(x, y, z - 0.3);

    const housingRingMid = new THREE.Mesh(
      new THREE.TorusGeometry(0.31 * scale, 0.025, 8, 26),
      housingDark
    );
    housingRingMid.position.set(x, y, z);

    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(0.28 * scale, 0.05, 8, 28),
      rimMat
    );
    rim.position.set(x, y, z + 0.36);

    const throat = new THREE.Mesh(
      new THREE.CylinderGeometry(0.22 * scale, 0.25 * scale, 0.36, 22),
      innerMat
    );
    throat.rotation.x = Math.PI / 2;
    throat.position.set(x, y, z + 0.22);

    const core = new THREE.Mesh(
      new THREE.CircleGeometry(0.19 * scale, 26),
      coreMat
    );
    core.position.set(x, y, z + 0.39);

    const plumeGeo = new THREE.ConeGeometry(
      0.23 * scale,
      1.15 * scale,
      22,
      1,
      true
    );
    const plume = new THREE.Mesh(plumeGeo, plumeMat);
    plume.rotation.x = Math.PI / 2;
    plume.position.set(x, y, z + 0.92 * scale);

    const intakeTop = new THREE.Mesh(
      new THREE.BoxGeometry(0.13 * scale, 0.055, 0.34),
      louverMat
    );
    intakeTop.position.set(x, y + 0.24 * scale, z);

    for (let i = 0; i < 4; i++) {
      const fin = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, 0.09, 0.04),
        louverMat
      );
      fin.position.set(x, y + 0.24 * scale, z - 0.14 + i * 0.095);
      parent.add(fin);
    }

    const sideVentLeft = new THREE.Mesh(
      new THREE.BoxGeometry(0.04, 0.05, 0.2),
      housingDark
    );
    sideVentLeft.position.set(
      x - 0.28 * scale - 0.02,
      y - 0.04,
      z - 0.05
    );
    const sideVentRight = sideVentLeft.clone();
    sideVentRight.position.x = x + 0.28 * scale + 0.02;

    parent.add(
      housing,
      housingRingFront,
      housingRingMid,
      rim,
      throat,
      core,
      plume,
      intakeTop,
      sideVentLeft,
      sideVentRight
    );

    return {
      local: new THREE.Vector3(x, y, z + 0.42),
      plume,
      core
    };
  };

  mounts.push(make(-0.36, 0.98));
  mounts.push(make(0.36, 0.98));

  return mounts;
}

function buildStrobes(
  parent: THREE.Object3D,
  skin: ShipSkin,
  strobes: THREE.Mesh[]
): void {
  const strobeMat = createGlowMaterial(skin.strobe, 1, true);
  const geo = new THREE.SphereGeometry(0.065, 10, 8);

  const left = new THREE.Mesh(geo, strobeMat);
  left.position.set(-1.74, 0.14, 0.78);
  const right = new THREE.Mesh(geo, strobeMat);
  right.position.set(1.74, 0.14, 0.78);

  const belly = new THREE.Mesh(
    new THREE.SphereGeometry(0.05, 10, 8),
    createGlowMaterial(skin.glow, 1, true)
  );
  belly.position.set(0, -0.2, 0);

  parent.add(left, right, belly);
  strobes.push(left, right, belly);
}
